import React, { useState, useRef, useCallback } from 'react';
import { Printer, X, Package, Trash2, Check, ChevronLeft, ChevronRight, Search } from 'lucide-react';

interface Variant {
  id: string;
  name: string;
  quality: string;
  condition: string;
  price: number;
  epc: string;
}

interface StockCard {
  id: string;
  stockCode: string;
  stockName: string;
  variants: Variant[];
}

interface BagItem {
  id: string;
  epc: string;
  acceptanceDate?: string;
  status?: string;
  bagCode?: string;
  totalCost?: number;
  stockCode?: string;
  stockName?: string;
  masterCode?: string;
  weight?: number;
}

interface BagDetailsPageProps {
  bags?: BagItem[];
  initialBagId?: string | null;
  onApprove: (bagId: string, splitWeight: number, epc: string) => void;
  onClose: () => void;
}

const isMixBag = (code?: string) => code?.startsWith('MIX') || code?.startsWith('BFS');

const BagDetailsPage: React.FC<BagDetailsPageProps> = ({ bags = [], initialBagId = null, onApprove, onClose }) => {
  const [selectedStockCard, setSelectedStockCard] = useState<string>('');
  const [selectedBagId, setSelectedBagId] = useState<string | null>(initialBagId || null);
  const [selectedBag, setSelectedBag] = useState<BagItem | null>(bags.find(b => b.id === initialBagId) || null);
  const [isReadingBag, setIsReadingBag] = useState(false);
  const [showBagReadSuccess, setShowBagReadSuccess] = useState(false);
  const [epcInput, setEpcInput] = useState('');
  const [showAllStockCards, setShowAllStockCards] = useState(false);
  const [stockCardSearch, setStockCardSearch] = useState('');
  const [showKeyboard, setShowKeyboard] = useState(false);
  const [capsLock, setCapsLock] = useState(true);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const mainStockScrollRef = useRef<HTMLDivElement>(null);
  const genderScrollRef = useRef<HTMLDivElement>(null);
  const categoryScrollRef = useRef<HTMLDivElement>(null);
  const [genderCardWidth, setGenderCardWidth] = useState(200);
  const subCatScrollRef = useRef<HTMLDivElement>(null);
  const [subCatCardWidth, setSubCatCardWidth] = useState(200);

  const genderScrollRefCallback = useCallback((node: HTMLDivElement | null) => {
    (genderScrollRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
    if (!node) return;
    const gap = 20;
    const w = (node.clientWidth - 2 * gap) / 3;
    setGenderCardWidth(Math.max(100, w));
  }, []);

  const subCatScrollRefCallback = useCallback((node: HTMLDivElement | null) => {
    (subCatScrollRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
    if (!node) return;
    const gap = 20;
    const w = (node.clientWidth - 2 * gap) / 3;
    setSubCatCardWidth(Math.max(100, w));
  }, []);

  const qualityScrollRef = useRef<HTMLDivElement>(null);
  const [qualityCardWidth, setQualityCardWidth] = useState(200);

  const qualityScrollRefCallback = useCallback((node: HTMLDivElement | null) => {
    (qualityScrollRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
    if (!node) return;
    const gap = 8;
    const w = (node.clientWidth - 8 * gap) / 9;
    setQualityCardWidth(Math.max(50, w));
  }, []);

  const scrollStockCards = useCallback((direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const scrollAmount = 320;
      scrollContainerRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  }, []);



  const scrollCategories = useCallback((direction: 'left' | 'right') => {
    if (categoryScrollRef.current) {
      const scrollAmount = 320;
      categoryScrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  }, []);

  const [_products] = useState<Variant[]>([]);
  const [addedVariants, setAddedVariants] = useState<
    Array<{
      gender?: string;
      category?: string;
      quality: string;
      price: number;
      weight: number;
      epc?: string;
      barcode?: string;
      productName: string;
      stockName?: string;
      size?: string;
      isDefective: boolean;
      stockCode?: string;
    }>
  >([]);

  const [selectedGender, setSelectedGender] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedSubCategory, setSelectedSubCategory] = useState<string>('');
  const [selectedSize, setSelectedSize] = useState<string>('');

  const [currentStep, setCurrentStep] = useState(0);
  const [maxReachedStep, setMaxReachedStep] = useState(0);
  const [selectedQuality, setSelectedQuality] = useState('');
  const [productPrice, setProductPrice] = useState('');
  const [productWeight, setProductWeight] = useState(0);
  const [productEpc, setProductEpc] = useState('');
  const [showApprovalSummary, setShowApprovalSummary] = useState(false);
  const [showCloseSuccess, setShowCloseSuccess] = useState(false);
  const [manualBalance, setManualBalance] = useState<number>(0);
  const [generatedBagTag, setGeneratedBagTag] = useState<string>('');
  const [isSplittingBag, setIsSplittingBag] = useState(false);
  const [showSplitModal, setShowSplitModal] = useState(false);
  const [splitWeightInput, setSplitWeightInput] = useState<string>('');
  const [splittingBagItem, setSplittingBagItem] = useState<BagItem | null>(null);

  const removeVariant = (index: number) => {
    setAddedVariants(prev => prev.filter((_, i) => i !== index));
  };

  const stockCards: StockCard[] = [
    { id: '1', stockCode: 'STK001', stockName: 'Klasik Fit Polo Yaka T-Shirt', variants: [] },
    { id: '2', stockCode: 'STK002', stockName: 'Slim Fit Uzun Kollu Gömlek', variants: [] },
    { id: '3', stockCode: 'STK003', stockName: 'Regular Fit Basic T-Shirt', variants: [] },
    { id: '4', stockCode: 'STK004', stockName: 'Regular Fit Denim Pantolon', variants: [] },
    { id: '5', stockCode: 'STK005', stockName: 'Slim Fit Blazer Ceket', variants: [] },
    { id: '6', stockCode: 'STK006', stockName: 'Pamuk Ham Madde', variants: [] },
    { id: '7', stockCode: 'STK07', stockName: 'Yün Karışımlı Kazak', variants: [] },
    { id: '8', stockCode: 'STK008', stockName: 'Keten Yazlık Gömlek', variants: [] },
    { id: '9', stockCode: 'STK009', stockName: 'Deri Mont Ceket', variants: [] },
    { id: '10', stockCode: 'STK010', stockName: 'Kargo Pantolon', variants: [] },
    { id: '11', stockCode: 'STK011', stockName: 'Kapüşonlu Sweatshirt', variants: [] },
    { id: '12', stockCode: 'STK012', stockName: 'Kadife Yelek', variants: [] },
    { id: '13', stockCode: 'MIX', stockName: 'Mix Çuval', variants: [] },
    { id: '14', stockCode: 'BFS', stockName: 'BFS Çuval', variants: [] }
  ];

  const isMix = isMixBag(selectedBag?.stockCode);

  const visibleStockCards = isMix
    ? stockCards.slice(0, 5)
    : selectedBag?.stockCode
      ? stockCards.filter(c => c.stockCode === selectedBag.stockCode)
      : stockCards.slice(0, 5);

  const filteredStockCards = stockCardSearch
    ? stockCards.filter(c =>
      c.stockName.toLowerCase().includes(stockCardSearch.toLowerCase()) ||
      c.stockCode.toLowerCase().includes(stockCardSearch.toLowerCase())
    )
    : stockCards;


  const generateBarcode = (code: string) =>
    `${code}${Math.floor(Math.random() * 1000).toString().padStart(4, '0')}`;

  const resetDraft = () => {
    setCurrentStep(0);
    setMaxReachedStep(0);
    setSelectedStockCard('');
    setSelectedGender('');
    setGenderPage(0);
    setSelectedCategory('');
    setSelectedSubCategory('');
    setSubCatPage(0);
    setSelectedSize('');
    setSelectedQuality('');
    setQualityPage(0);
    setProductPrice('');
    setProductWeight(0);
    setProductWeight(0);
    setProductEpc('');
    setIsSplittingBag(false);
  };

  // RFID ile çuval seçme (simülasyon)
  const startRfidSelect = async () => {
    if (!bags || bags.length === 0) { alert('Kayıtlı çuval yok'); return; }
    setIsReadingBag(true);
    await new Promise(res => setTimeout(res, 900));
    const found = bags.find(b => b.status === 'Kabul Edildi') || bags[0] || null;
    if (found) {
      setSelectedBagId(found.id);
      setSelectedBag(found);
      if (maxReachedStep < 1) setMaxReachedStep(1);
      setCurrentStep(1);
      setShowBagReadSuccess(true);
      setTimeout(() => setShowBagReadSuccess(false), 1200);
    } else {
      alert('Eşleşen çuval bulunamadı');
    }
    setIsReadingBag(false);
  };

  const handleEpcSubmit = () => {
    if (!epcInput.trim()) { alert('Lütfen EPC girin'); return; }
    const search = epcInput.trim();
    const found = bags.find(b => (b.epc || '').includes(search) || (b.bagCode || '').includes(search));
    if (!found) { alert('Eşleşen çuval bulunamadı'); return; }
    setSelectedBagId(found.id);
    setSelectedBag(found);
    if (maxReachedStep < 1) setMaxReachedStep(1);
    setCurrentStep(1);
    setEpcInput('');
    setShowBagReadSuccess(true);
    setTimeout(() => setShowBagReadSuccess(false), 1200);
  };

  const selectBag = (bag: BagItem) => {
    setSelectedBagId(bag.id);
    setSelectedBag(bag);
    // Reset products when selecting a different bag
    if (selectedBagId !== bag.id) {
      setAddedVariants([]);
      setMaxReachedStep(0);
      setCurrentStep(0);
      setSelectedStockCard('');
      setSelectedGender('');
      setSelectedCategory('');
      setSelectedSubCategory('');
      setSelectedSize('');
      setSelectedQuality('');
      setProductWeight(0);
      setProductEpc('');
    }
    // Don't navigate to step 1 yet - just preview the bag
  };

  const handleSplitBag = (bag: BagItem) => {
    setSplittingBagItem(bag);
    setSplitWeightInput('');
    setShowSplitModal(true);
  };

  const confirmSplit = () => {
    if (!splittingBagItem || !splitWeightInput) return;
    const splitWeight = Number(splitWeightInput);
    if (isNaN(splitWeight) || splitWeight <= 0) {
      alert('Lütfen geçerli bir ağırlık girin');
      return;
    }

    if (splitWeight >= (splittingBagItem.weight || 0)) {
      alert('Parçalama ağırlığı mevcut ağırlıktan küçük olmalıdır');
      return;
    }

    const randomHex = Math.floor(Math.random() * 65535).toString(16).toUpperCase().padStart(4, '0');
    const newEpc = `3074257BF7194E400000${randomHex}`;

    onApprove(
      splittingBagItem.id,
      splitWeight,
      newEpc
    );

    setShowSplitModal(false);
    setSplittingBagItem(null);
    setSplitWeightInput('');
  };



  const confirmBagSelection = () => {
    if (!selectedBag) return;

    const isMix = isMixBag(selectedBag.stockCode);

    if (!isMix && selectedBag.stockCode) {
      const card = stockCards.find(c => c.stockCode === selectedBag.stockCode);
      if (card) {
        setSelectedStockCard(card.id);
      }
    }

    if (maxReachedStep < 1) setMaxReachedStep(1);
    setCurrentStep(1);
    setShowBagReadSuccess(true);
    setTimeout(() => setShowBagReadSuccess(false), 900);
  };

  const completeStepSelection = (_current: number, next: number) => {
    if (next > maxReachedStep) {
      setMaxReachedStep(next);
      setCurrentStep(next);
    } else {
      setCurrentStep(next);
    }
  };

  const qualityOptions = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12', '13', '14', '15'];
  const priceOptions = [100, 250, 500, 750, 1000, 1500, 2000, 3000, 5000];

  const genders = [
    { id: 'erkek', label: 'ERKEK', icon: '👨', color: 'blue' },
    { id: 'kadin', label: 'KADIN', icon: '👩', color: 'rose' },
    { id: 'unisex', label: 'UNISEX', icon: '👫', color: 'purple' },
    { id: 'erkek_cocuk', label: 'ERKEK ÇOCUK', icon: '👦', color: 'cyan' },
    { id: 'kiz_cocuk', label: 'KIZ ÇOCUK', icon: '👧', color: 'pink' }
  ];

  const [genderPage, setGenderPage] = useState(0);
  const maxGenderPage = Math.max(0, genders.length - 3);

  const scrollGenders = useCallback((direction: 'left' | 'right') => {
    setGenderPage(prev => {
      if (direction === 'left') return Math.max(0, prev - 1);
      return Math.min(maxGenderPage, prev + 1);
    });
  }, [maxGenderPage]);

  const categories = [
    { id: 'tshirt', label: 'T-Shirt', icon: '👕' },
    { id: 'pantolon', label: 'Pantolon', icon: '👖' },
    { id: 'ceket', label: 'Ceket', icon: '🧥' },
    { id: 'gomlek', label: 'Gömlek', icon: '👔' },
    { id: 'etek', label: 'Etek', icon: '👗' },
    { id: 'elbise', label: 'Elbise', icon: '👗' },
    { id: 'kazak', label: 'Kazak', icon: '🧶' },
    { id: 'hirka', label: 'Hırka', icon: '🧥' },
  ];

  const subCategoriesData: { [key: string]: string[] } = {
    'T-Shirt': ['Basic', 'Polo', 'V-Yaka', 'Oversize'],
    'Pantolon': ['Kot 1', 'Kumaş', 'Kanvas', 'Eşofman'],
    'Ceket': ['Deri', 'Kot', 'Blazer', 'Şişme'],
    'Gömlek': ['Klasik', 'Slim Fit', 'Keten', 'Oduncu'],
    'Etek': ['Mini', 'Midi', 'Maksi', 'Pileli'],
    'Elbise': ['Gece', 'Günlük', 'Yazlık', 'Triko'],
    'Kazak': ['Yünlü', 'Akrilik', 'Balıkçı', 'V-Yaka'],
    'Hırka': ['Düğmeli', 'Fermuarlı', 'Uzun', 'Triko'],
  };

  const currentSubCategories = subCategoriesData[selectedCategory] || [];
  const [subCatPage, setSubCatPage] = useState(0);
  const maxSubCatPage = Math.max(0, currentSubCategories.length - 3);

  const scrollSubCategories = useCallback((direction: 'left' | 'right') => {
    setSubCatPage(prev => {
      if (direction === 'left') return Math.max(0, prev - 1);
      return Math.min(maxSubCatPage, prev + 1);
    });
  }, [maxSubCatPage]);

  const [qualityPage, setQualityPage] = useState(0);
  const qualitiesPerPage = 9;
  const maxQualityPage = Math.max(0, Math.ceil(qualityOptions.length / qualitiesPerPage) - 1);

  const scrollQualities = useCallback((direction: 'left' | 'right') => {
    setQualityPage(prev => {
      if (direction === 'left') return Math.max(0, prev - 1);
      return Math.min(maxQualityPage, prev + 1);
    });
  }, [maxQualityPage]);

  const sizes = [
    '3XS', 'XXS', 'XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL',
    '32', '34', '36', '38', '40', '42', '44', '46', '48', '50', '52', '54', '56', '58',
    '24', '26', '28', '30', '31', '32', '33', '34', '36', '38'
  ];

  const getQualityColorAttributes = (q: string) => {
    const num = parseInt(q);
    if (num <= 2) return {
      active: 'border-green-600 bg-white text-green-700 shadow-xl',
      inactive: 'border-green-100 bg-white text-green-600 hover:bg-green-50',
      badge: 'bg-green-100 text-green-700'
    };
    if (num <= 4) return {
      active: 'border-blue-600 bg-white text-blue-700 shadow-xl',
      inactive: 'border-blue-100 bg-white text-blue-600 hover:bg-blue-50',
      badge: 'bg-blue-100 text-blue-700'
    };
    if (num <= 6) return {
      active: 'border-orange-500 bg-white text-orange-700 shadow-xl',
      inactive: 'border-orange-100 bg-white text-orange-600 hover:bg-orange-50',
      badge: 'bg-orange-100 text-orange-700'
    };
    return {
      active: 'border-red-600 bg-white text-red-700 shadow-xl',
      inactive: 'border-red-100 bg-white text-red-600 hover:bg-red-50',
      badge: 'bg-red-100 text-red-700'
    };
  };

  const handleNextStep = () => {
    if (currentStep === 1 && !selectedStockCard) {
      alert('Lütfen stok kartı seçiniz');
      return;
    }
    if (currentStep === 2 && !selectedGender) {
      alert('Lütfen cinsiyet seçiniz');
      return;
    }
    if (currentStep === 3 && !selectedCategory) {
      alert('Lütfen kategori seçiniz');
      return;
    }
    if (currentStep === 4 && !selectedSubCategory) {
      alert('Lütfen ürün türünü seçiniz');
      return;
    }
    if (currentStep === 5 && !selectedQuality) {
      alert('Lütfen kalite seçiniz');
      return;
    }
    if (currentStep === 6 && !selectedSize) { alert('Lütfen beden seçiniz'); return; }
    if (productWeight <= 0) { alert('Ağırlık sıfırdan büyük olmalıdır'); return; }
    if (!productEpc.trim()) { alert('EPC girilmelidir'); return; }

    // Direct addition logic instead of intermediate steps
    const stockCard = stockCards.find(card => card.id === selectedStockCard);
    const newEpc = productEpc; // Don't generate random if empty
    const newBarcode = generateBarcode(stockCard?.stockCode ?? '0000');

    setAddedVariants(prev => [
      ...prev,
      {
        gender: selectedGender,
        category: selectedCategory,
        quality: selectedQuality,
        price: Number(productPrice) || 500,
        weight: productWeight,
        epc: newEpc,
        barcode: newBarcode,
        productName: `${selectedSubCategory} ${selectedCategory}`,
        stockName: stockCard?.stockName || '',
        size: selectedSize,
        isDefective: false
      }
    ]);

    setProductWeight(0);
    setProductEpc('');

    // Reset only ephemeral product values for the next item
    setProductWeight(0);
    setProductEpc('');

    // We stay on the current step (likely Step 6) so the user can quickly add another item
    // of the same type by just entering weight and EPC.
    // they can manually go back to other steps via sidebar if needed.
  };

  const handleCloseBag = () => {
    if (addedVariants.length === 0) { alert('Kapatmak için önce ürün eklemelidir'); return; }

    const newTag = `BAG-${Math.floor(100000 + Math.random() * 900000)}`;
    setGeneratedBagTag(newTag);

    const totalWeightKG = totalWeight / 1000;
    if (Math.abs(totalWeightKG - expectedWeight) >= 0.01) {
      setManualBalance(Number((expectedWeight - totalWeightKG).toFixed(2)));
      setShowApprovalSummary(true);
    } else {
      console.log(`Etiket yazdırılıyor: ${newTag}`);
      setShowCloseSuccess(true);
      setTimeout(() => {
        setShowCloseSuccess(false);
        setSelectedBag(null);
        setSelectedBagId(null);
        setAddedVariants([]);
        setGeneratedBagTag('');
        resetDraft();
      }, 2000);
    }
  };

  const totalWeight = addedVariants.reduce((sum, item) => sum + item.weight, 0);
  // const totalProductValue = addedVariants.reduce((sum, item) => sum + item.price, 0); // Lint fix
  const expectedWeight = selectedBag?.weight || 60;
  const remainingWeight = expectedWeight - (totalWeight / 1000);
  // const balance = expectedWeight - totalWeight; // Lint fix: balance unused
  const productCount = addedVariants.length;

  const normalVariants = addedVariants.filter(v => !v.isDefective);
  const normalCount = normalVariants.length;
  // const averagePrice = normalCount > 0 ? totalProductValue / normalCount : 0; // Lint fix

  const sumQuality = normalVariants.reduce((sum, item) => {
    const q = parseInt(item.quality);
    return sum + (isNaN(q) ? 0 : q);
  }, 0);
  const averageQuality = normalCount > 0 ? sumQuality / normalCount : 0;

  return (
    <div className="h-screen overflow-hidden bg-[#F8FAFC] p-4 sm:p-6 flex flex-col gap-4">
      {/* Çuval Bilgileri header removed to avoid extra top white area */}
      <div className="h-2" />

      {/* Main Integrated Workspace */}
      <div className="flex-1 flex flex-col lg:flex-row gap-6 overflow-hidden min-h-0">
        {/* Left Menu Sidebar */}
        <div className="w-full lg:w-64 min-h-0 flex flex-col shrink-0">
          <div className="bg-white shadow-xl p-6 border border-slate-100 h-full flex flex-col min-h-[95vh]">
            <h4 className="text-[10px] font-black text-slate-400 mb-6 uppercase tracking-[0.2em]">İŞLEM ADIMLARI</h4>

            <nav className="flex flex-col gap-3">
              {[
                { id: 0, label: 'Çuval Seçimi', icon: '👜' },
                { id: 1, label: 'Stok Kartı', icon: '📑' },
                { id: 2, label: 'Cinsiyet', icon: '👤' },
                { id: 3, label: 'Kategori', icon: '📂' },
                { id: 4, label: 'Ürün Türü', icon: '🏷️' },
                { id: 5, label: 'Kalite', icon: '⭐' },
                { id: 6, label: 'Beden', icon: '📏' },
              ].map((step) => {
                const isSelected = currentStep === step.id;
                const isPast = step.id <= maxReachedStep;

                return (
                  <button
                    key={step.id}
                    onClick={() => {
                      if (isPast || step.id === 0) setCurrentStep(step.id);
                    }}
                    className={`group relative flex items-center gap-3 px-5 py-4 rounded-2xl text-sm font-black transition-all duration-300 ${isSelected
                      ? 'bg-blue-600 text-white shadow-xl shadow-blue-200 translate-x-1'
                      : isPast || step.id === 0
                        ? 'bg-white text-slate-700 hover:bg-slate-50 border-2 border-slate-50 shadow-sm'
                        : 'bg-slate-50/50 text-slate-400 border-2 border-slate-100/50 cursor-not-allowed'
                      }`}
                  >
                    <span className="text-lg">{step.icon}</span>
                    <span className="flex-1 text-left line-clamp-1">{step.label}</span>
                    {isSelected && (
                      <div className="w-1.5 h-1.5 rounded-full bg-white shadow-sm" />
                    )}
                    {!isSelected && isPast && (
                      <div className="w-1.5 h-1.5 rounded-full bg-green-400" />
                    )}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Center Workspace (Selection + DataGrid) */}
        <div className="flex-1 flex flex-col gap-6 min-h-0 min-w-0">
          {/* Top: Selection Area */}
          <div className="bg-white shadow-xl border border-slate-100 p-8 flex flex-col flex-[0_0_auto] overflow-hidden">
            <div className="flex justify-between items-center mb-6">
              <h4 className="text-2xl font-black text-slate-800 uppercase">
                {currentStep === 0 && 'ÇUVAL SEÇİN'}
                {currentStep === 1 && 'STOK KARTI SEÇİN'}
                {currentStep === 2 && 'CİNSİYET SEÇİN'}
                {currentStep === 3 && 'KATEGORİ SEÇİN'}
                {currentStep === 4 && 'ÜRÜN TÜRÜ SEÇİN'}
                {currentStep === 5 && 'KALİTE SEÇİN'}
                {currentStep === 6 && 'BEDEN SEÇİN'}
              </h4>
              <div className="flex gap-2">
                {[0, 1, 2, 3, 4, 5, 6].map(s => (
                  <div key={s} className={`w-3 h-3 rounded-full ${currentStep === s ? 'bg-blue-600' : s < maxReachedStep ? 'bg-blue-200' : 'bg-slate-100'}`}></div>
                ))}
              </div>
            </div>

            {/* Selection Content - Moved UP with padding-top change */}
            <div className="flex-1 flex flex-col justify-start pt-4">
              <>
                {currentStep === 0 && (
                  <div className="flex flex-col gap-6 animate-in fade-in slide-in-from-bottom-4 duration-500 h-full">
                    {/* Page 1: Bag List (when no bag is selected) */}
                    {!selectedBag && (
                      <>
                        {/* Top: RFID Button and Barcode Search */}
                        <div className="flex items-center gap-4">
                          <button
                            onClick={startRfidSelect}
                            disabled={isReadingBag}
                            className={`px-6 py-3 rounded-xl font-bold text-sm transition-all flex items-center gap-2 ${isReadingBag
                              ? 'bg-blue-100 text-blue-400 cursor-wait'
                              : 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg shadow-blue-200'
                              }`}
                          >
                            <span>{isReadingBag ? '⏳' : '📡'}</span>
                            {isReadingBag ? 'Okunuyor...' : 'RFID ile Aç'}
                          </button>

                          <div className="flex-1 max-w-md">
                            <input
                              type="text"
                              value={epcInput}
                              onChange={(e) => setEpcInput(e.target.value)}
                              onKeyDown={(e) => e.key === 'Enter' && handleEpcSubmit()}
                              placeholder="Barkod ile ara..."
                              className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-sm font-medium text-slate-700 focus:border-blue-500 outline-none transition-all placeholder:text-slate-400 bg-white"
                            />
                          </div>
                        </div>

                        {/* Bags DataGrid Table */}
                        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex-1 flex flex-col min-h-[400px]">
                          <div className="overflow-auto flex-1">
                            <table className="w-full">
                              <thead className="sticky top-0 bg-white z-10">
                                <tr className="border-b border-slate-100">
                                  <th className="text-left px-6 py-4 text-sm font-bold text-slate-600">Barkod</th>
                                  <th className="text-left px-6 py-4 text-sm font-bold text-slate-600">Stok Kodu</th>
                                  <th className="text-left px-6 py-4 text-sm font-bold text-slate-600">Stok Açıklaması</th>
                                  <th className="text-left px-6 py-4 text-sm font-bold text-slate-600">Kabul Tarihi</th>
                                  <th className="text-left px-6 py-4 text-sm font-bold text-slate-600 text-center">Ağırlık (KG)</th>
                                  <th className="text-left px-6 py-4 text-sm font-bold text-slate-600">Durum</th>
                                  <th className="text-left px-6 py-4 text-sm font-bold text-slate-600">Açılma Tarihi</th>
                                  <th className="text-left px-6 py-4 text-sm font-bold text-slate-600">İşlem</th>
                                </tr>
                              </thead>
                              <tbody>
                                {bags.filter(bag =>
                                  !epcInput ||
                                  bag.epc.toLowerCase().includes(epcInput.toLowerCase()) ||
                                  (bag.bagCode && bag.bagCode.toLowerCase().includes(epcInput.toLowerCase()))
                                ).map((bag, index) => (
                                  <tr
                                    key={bag.id}
                                    onClick={() => selectBag(bag)}
                                    className="border-b border-slate-50 hover:bg-blue-50/50 transition-colors cursor-pointer"
                                  >
                                    <td className="px-6 py-4">
                                      <span className="font-mono text-sm font-semibold text-slate-700">{bag.epc}</span>
                                    </td>
                                    <td className="px-6 py-4">
                                      <span className="text-xs font-black text-blue-600 bg-blue-50 px-2 py-1 rounded-lg">
                                        {bag.stockCode || `STK00${(index % 9) + 1}`}
                                      </span>
                                    </td>
                                    <td className="px-6 py-4">
                                      <span className="text-sm font-semibold text-slate-700">
                                        {bag.stockName}
                                      </span>
                                    </td>
                                    <td className="px-6 py-4">
                                      <span className="text-sm text-slate-600">{bag.acceptanceDate}</span>
                                    </td>
                                    <td className="px-6 py-4 text-center">
                                      <span className="text-sm font-black text-blue-600 bg-blue-50 px-3 py-1 rounded-full">
                                        {(bag.weight || 0).toFixed(2)}
                                      </span>
                                    </td>
                                    <td className="px-6 py-4">
                                      <span className="inline-flex px-3 py-1 bg-green-100 text-green-700 text-xs font-bold rounded-full">
                                        Kabul Edildi
                                      </span>
                                    </td>
                                    <td className="px-6 py-4">
                                      <span className="text-sm text-slate-400">-</span>
                                    </td>
                                    <td className="px-6 py-4">
                                      <div className="flex gap-4">
                                        <button
                                          onClick={(e) => { e.stopPropagation(); selectBag(bag); }}
                                          className="px-4 py-1.5 rounded-lg text-xs font-bold transition-colors bg-blue-600 text-white hover:bg-blue-700"
                                        >
                                          Seç
                                        </button>
                                        <button
                                          onClick={(e) => { e.stopPropagation(); handleSplitBag(bag); }}
                                          className="px-4 py-1.5 rounded-lg text-xs font-bold transition-colors bg-white text-emerald-600 border border-emerald-600 hover:bg-emerald-50"
                                        >
                                          Çuval Parçala
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>

                          {/* Empty state */}
                          {bags.filter(bag =>
                            !epcInput ||
                            bag.epc.toLowerCase().includes(epcInput.toLowerCase()) ||
                            (bag.bagCode && bag.bagCode.toLowerCase().includes(epcInput.toLowerCase()))
                          ).length === 0 && (
                              <div className="py-12 text-center">
                                <div className="text-4xl mb-2">📦</div>
                                <p className="text-slate-400 font-medium">Arama sonucu bulunamadı</p>
                              </div>
                            )}
                        </div>
                      </>
                    )}

                    {/* Page 2: Selected Bag Details */}
                    {selectedBag && (
                      <div className="flex flex-col gap-6 h-full animate-in fade-in slide-in-from-right-4 duration-300">
                        {/* Back Button */}
                        <button
                          onClick={() => { setSelectedBag(null); setSelectedBagId(''); }}
                          className="self-start flex items-center gap-2 px-4 py-2 text-slate-600 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-colors font-bold text-sm"
                        >
                          <span>←</span>
                          Çuval Listesine Dön
                        </button>

                        {/* Bag Details Card */}
                        <div className="bg-white rounded-2xl border border-slate-200 shadow-lg p-8 flex-1">
                          {/* Header */}
                          <div className="flex items-start justify-between mb-8">
                            <div className="flex items-center gap-4">
                              <div className="w-16 h-16 rounded-2xl bg-blue-600 flex items-center justify-center text-white text-3xl shadow-lg shadow-blue-200">
                                📦
                              </div>
                              <div>
                                <div className="text-xs font-black text-blue-500 uppercase tracking-widest mb-2">SEÇİLİ ÇUVAL</div>
                                <div className="text-lg font-black text-slate-800 break-all">{selectedBag.epc}</div>
                              </div>
                            </div>
                          </div>

                          {/* Details Grid */}
                          <div className="grid grid-cols-3 gap-6 mb-8">
                            <div className="bg-slate-50 rounded-2xl p-5 border border-slate-100">
                              <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wide mb-2">Durum</div>
                              <div className="flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full bg-green-500"></span>
                                <span className="text-sm font-black text-green-600">Kabul Edildi</span>
                              </div>
                            </div>
                            <div className="bg-slate-50 rounded-2xl p-5 border border-slate-100">
                              <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wide mb-2">Kabul Tarihi</div>
                              <div className="text-sm font-black text-slate-700">2025-12-04</div>
                            </div>
                            <div className="bg-slate-50 rounded-2xl p-5 border border-slate-100">
                              <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wide mb-2">Hedef Ağırlık</div>
                              <div className="text-sm font-black text-slate-700">{expectedWeight} KG</div>
                            </div>
                          </div>

                          {/* Action Button */}
                          <div className="flex justify-center">
                            {addedVariants.length > 0 ? (
                              <button
                                onClick={handleCloseBag}
                                className="px-12 py-5 bg-red-600 text-white rounded-2xl text-lg font-black uppercase tracking-wide hover:bg-red-700 transition-all shadow-xl shadow-red-200 flex items-center gap-4"
                              >
                                <span className="text-2xl">🔒</span>
                                Çuvalı Kapat
                              </button>
                            ) : (
                              <button
                                onClick={confirmBagSelection}
                                className="px-12 py-5 bg-green-600 text-white rounded-2xl text-lg font-black uppercase tracking-wide hover:bg-green-700 transition-all shadow-xl shadow-green-200 flex items-center gap-4"
                              >
                                <span className="text-2xl">🔓</span>
                                Çuvalı Aç
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {currentStep === 1 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                    {visibleStockCards.map(card => (
                      <button key={card.id} onClick={() => { setSelectedStockCard(card.id); completeStepSelection(1, 2); }} className={`p-6 rounded-[2rem] border-4 text-left transition-all ${selectedStockCard === card.id ? 'border-blue-600 bg-blue-50/20 shadow-xl' : 'border-slate-50 bg-white hover:border-blue-100'}`}>
                        <div className="w-12 h-12 bg-slate-50 rounded-2xl flex items-center justify-center text-2xl mb-4">📑</div>
                        <span className="text-[10px] font-black text-slate-400 uppercase">{card.stockCode}</span>
                        <div className="text-lg font-black text-slate-700 leading-tight">{card.stockName}</div>
                      </button>
                    ))}
                    <button
                      onClick={() => { setShowAllStockCards(true); setStockCardSearch(''); }}
                      className="p-6 rounded-[2rem] border-4 border-dashed border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50 text-left transition-all hover:border-blue-400 hover:shadow-xl hover:scale-[1.02] group"
                    >
                      <div className="w-12 h-12 bg-blue-100 rounded-2xl flex items-center justify-center text-2xl mb-4 group-hover:bg-blue-200 transition-colors">
                        <Search className="w-6 h-6 text-blue-600" />
                      </div>
                      <span className="text-[10px] font-black text-blue-400 uppercase">TÜM KARTLAR</span>
                      <div className="text-lg font-black text-blue-600 leading-tight">Diğerleri</div>
                      <div className="text-[10px] font-bold text-blue-400 mt-1">{stockCards.length - visibleStockCards.length} kart daha</div>
                    </button>
                  </div>
                )}

                {currentStep === 2 && (
                  <div className="relative flex items-center gap-3 animate-in fade-in slide-in-from-bottom-4 duration-500">
                    <button
                      onClick={() => scrollGenders('left')}
                      className="shrink-0 w-12 h-12 bg-white border-2 border-slate-100 hover:border-blue-500 hover:bg-blue-50 rounded-2xl flex items-center justify-center text-slate-400 hover:text-blue-600 transition-all shadow-lg z-10"
                    >
                      <ChevronLeft className="w-7 h-7" />
                    </button>

                    <div
                      ref={genderScrollRefCallback}
                      className="flex-1 overflow-hidden py-4 min-w-0"
                    >
                      <div
                        className="flex gap-5 transition-transform duration-500 ease-in-out"
                        style={{ transform: `translateX(-${genderPage * (genderCardWidth + 20)}px)` }}
                      >
                        {genders.map(g => (
                          <button
                            key={g.id}
                            onClick={() => { setSelectedGender(g.label); completeStepSelection(2, 3); }}
                            style={{ width: genderCardWidth, minWidth: genderCardWidth }}
                            className={`shrink-0 flex flex-col items-center p-6 rounded-[2rem] border-4 transition-all duration-300 ${selectedGender === g.label ? 'border-blue-600 bg-white shadow-2xl scale-[1.03]' : 'border-slate-50 bg-slate-50/20 hover:border-blue-100 hover:bg-white'}`}
                          >
                            <span className="text-6xl mb-4 drop-shadow-lg">{g.icon}</span>
                            <span className="text-xl font-black uppercase text-slate-700 tracking-wider">{g.label}</span>
                            {selectedGender === g.label && (
                              <div className="mt-3 flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded-full">
                                <Check className="w-4 h-4" />
                                <span className="text-[10px] font-black uppercase tracking-widest text-white">SEÇİLİ</span>
                              </div>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>

                    <button
                      onClick={() => scrollGenders('right')}
                      className="shrink-0 w-12 h-12 bg-white border-2 border-slate-100 hover:border-blue-500 hover:bg-blue-50 rounded-2xl flex items-center justify-center text-slate-400 hover:text-blue-600 transition-all shadow-lg z-10"
                    >
                      <ChevronRight className="w-7 h-7" />
                    </button>
                  </div>
                )}

                {currentStep === 3 && (
                  <div className="relative flex items-center gap-4 animate-in fade-in slide-in-from-bottom-4 duration-500">
                    <button
                      onClick={() => scrollCategories('left')}
                      className="shrink-0 w-16 h-16 bg-white border-2 border-slate-100 hover:border-blue-500 hover:bg-blue-50 rounded-[1.5rem] flex items-center justify-center text-slate-400 hover:text-blue-600 transition-all shadow-lg z-10"
                    >
                      <ChevronLeft className="w-10 h-10" />
                    </button>

                    <div
                      ref={categoryScrollRef}
                      className="flex-1 overflow-x-auto overflow-y-hidden flex gap-6 py-6 px-4 scroll-smooth no-scrollbar"
                      style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                    >
                      {categories.map(cat => (
                        <button
                          key={cat.id}
                          onClick={() => { setSelectedCategory(cat.label); setSubCatPage(0); completeStepSelection(3, 4); }}
                          className={`shrink-0 w-[240px] flex flex-col items-center p-8 rounded-[3rem] border-4 transition-all ${selectedCategory === cat.label ? 'border-blue-600 bg-white shadow-xl scale-[1.03]' : 'border-slate-50 bg-slate-50 hover:border-blue-100 hover:bg-white'}`}
                        >
                          <span className="text-7xl mb-6 drop-shadow-md">{cat.icon}</span>
                          <span className="text-2xl font-black text-slate-700 uppercase tracking-tight">{cat.label}</span>
                          {selectedCategory === cat.label && (
                            <div className="mt-4 flex items-center gap-2 px-3 py-1 bg-blue-600 text-white rounded-full">
                              <Check className="w-4 h-4" />
                              <span className="text-[10px] font-black uppercase tracking-widest text-white">SEÇİLİ</span>
                            </div>
                          )}
                        </button>
                      ))}
                    </div>

                    <button
                      onClick={() => scrollCategories('right')}
                      className="shrink-0 w-16 h-16 bg-white border-2 border-slate-100 hover:border-blue-500 hover:bg-blue-50 rounded-[1.5rem] flex items-center justify-center text-slate-400 hover:text-blue-600 transition-all shadow-lg z-10"
                    >
                      <ChevronRight className="w-10 h-10" />
                    </button>
                  </div>
                )}

                {currentStep === 4 && (
                  <div className="relative flex items-center gap-3 animate-in fade-in slide-in-from-bottom-4 duration-500">
                    <button
                      onClick={() => scrollSubCategories('left')}
                      className="shrink-0 w-12 h-12 bg-white border-2 border-slate-100 hover:border-blue-500 hover:bg-blue-50 rounded-2xl flex items-center justify-center text-slate-400 hover:text-blue-600 transition-all shadow-lg z-10"
                    >
                      <ChevronLeft className="w-7 h-7" />
                    </button>

                    <div
                      ref={subCatScrollRefCallback}
                      className="flex-1 overflow-hidden py-4 min-w-0"
                    >
                      <div
                        className="flex gap-5 transition-transform duration-500 ease-in-out"
                        style={{ transform: `translateX(-${subCatPage * (subCatCardWidth + 20)}px)` }}
                      >
                        {currentSubCategories.map(sub => (
                          <button
                            key={sub}
                            onClick={() => { setSelectedSubCategory(sub); completeStepSelection(4, 5); }}
                            style={{ width: subCatCardWidth, minWidth: subCatCardWidth }}
                            className={`shrink-0 flex flex-col items-center justify-center p-6 min-h-[120px] rounded-[2rem] border-4 transition-all duration-300 ${selectedSubCategory === sub ? 'border-blue-600 bg-white shadow-2xl scale-[1.03]' : 'border-slate-50 bg-slate-50/20 hover:border-blue-100 hover:bg-white'}`}
                          >
                            <span className="text-xl font-black text-slate-700 text-center leading-tight uppercase">{sub}</span>
                            {selectedSubCategory === sub && (
                              <div className="mt-3 flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded-full">
                                <Check className="w-4 h-4" />
                                <span className="text-[10px] font-black uppercase tracking-widest text-white">SEÇİLİ</span>
                              </div>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>

                    <button
                      onClick={() => scrollSubCategories('right')}
                      className="shrink-0 w-12 h-12 bg-white border-2 border-slate-100 hover:border-blue-500 hover:bg-blue-50 rounded-2xl flex items-center justify-center text-slate-400 hover:text-blue-600 transition-all shadow-lg z-10"
                    >
                      <ChevronRight className="w-7 h-7" />
                    </button>
                  </div>
                )}

                {currentStep === 5 && (
                  <div className="relative flex items-center gap-2 animate-in fade-in slide-in-from-bottom-4 duration-500">
                    <button
                      onClick={() => scrollQualities('left')}
                      disabled={qualityPage === 0}
                      className={`shrink-0 w-10 h-10 bg-white border-2 rounded-xl flex items-center justify-center transition-all shadow-md z-10 ${qualityPage === 0 ? 'border-slate-100 text-slate-200 cursor-not-allowed' : 'border-slate-200 hover:border-blue-500 hover:bg-blue-50 text-slate-400 hover:text-blue-600'}`}
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>

                    <div
                      ref={qualityScrollRefCallback}
                      className="flex-1 overflow-hidden py-2 min-w-0"
                    >
                      <div
                        className="flex gap-2 transition-transform duration-500 ease-in-out"
                        style={{ transform: `translateX(-${qualityPage * qualitiesPerPage * (qualityCardWidth + 8)}px)` }}
                      >
                        {qualityOptions.map(q => {
                          const colors = getQualityColorAttributes(q);
                          return (
                            <button
                              key={q}
                              onClick={() => setSelectedQuality(q)}
                              style={{ width: qualityCardWidth, minWidth: qualityCardWidth }}
                              className={`shrink-0 flex flex-col items-center py-3 px-1 rounded-xl border-2 transition-all duration-300 ${selectedQuality === q ? colors.active + ' scale-[1.05]' : colors.inactive + ' hover:scale-105'}`}
                            >
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-1.5 border-2 ${colors.badge} shadow-inner`}>
                                <span className="text-base font-black">{q}</span>
                              </div>
                              <span className="text-[8px] font-black text-slate-500 uppercase tracking-widest">KALİTE</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <button
                      onClick={() => scrollQualities('right')}
                      disabled={qualityPage >= maxQualityPage}
                      className={`shrink-0 w-10 h-10 bg-white border-2 rounded-xl flex items-center justify-center transition-all shadow-md z-10 ${qualityPage >= maxQualityPage ? 'border-slate-100 text-slate-200 cursor-not-allowed' : 'border-slate-200 hover:border-blue-500 hover:bg-blue-50 text-slate-400 hover:text-blue-600'}`}
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </div>
                )}

                {currentStep === 6 && (
                  <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-3 animate-in fade-in slide-in-from-bottom-4 duration-500 overflow-y-auto max-h-[400px] pr-2 custom-scrollbar">
                    {sizes.map(size => (
                      <button
                        key={size}
                        onClick={() => {
                          setSelectedSize(size);
                          if (isSplittingBag) {
                            // Simulate weight: between 150g and 1200g
                            setProductWeight(Math.floor(150 + Math.random() * 1050));
                            setProductEpc('-');
                          }
                        }}
                        className={`flex flex-col items-center justify-center py-4 px-2 rounded-2xl border-2 transition-all hover:scale-[1.05] ${selectedSize === size
                          ? 'border-blue-600 bg-blue-50/30 shadow-md ring-2 ring-blue-600/10'
                          : 'border-slate-100 bg-white hover:border-blue-200'
                          }`}
                      >
                        <span className={`font-black uppercase tracking-tight ${selectedSize === size ? 'text-blue-600 text-xl' : 'text-slate-700 text-lg'
                          }`}>{size}</span>
                        {selectedSize === size && (
                          <div className="mt-1 w-1.5 h-1.5 rounded-full bg-blue-600 animate-pulse" />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </>

              {currentStep >= 7 && (
                <div className="text-center py-20">
                  <div className="w-20 h-20 border-8 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
                  <h4 className="text-3xl font-black text-blue-600 uppercase">İşlem Yapılıyor...</h4>
                </div>
              )}
            </div>
          </div>

          {/* Bottom: Bag List DataGrid / Price Selection */}
          {currentStep === 5 && selectedQuality ? (
            <div className="flex-1 bg-white shadow-sm p-8 border border-slate-100 flex flex-col min-h-0 relative animate-in fade-in slide-in-from-bottom-4 duration-500">
              <div className="flex items-center gap-4 mb-6 shrink-0">
                <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center text-white text-xl shadow-lg">💰</div>
                <h4 className="text-xl font-black text-slate-800 uppercase">FİYAT SEÇİN</h4>
              </div>
              <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar">
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-6">
                  {priceOptions.map(price => (
                    <button key={price} onClick={() => { setProductPrice(price.toString()); completeStepSelection(5, 6); }} className={`p-6 rounded-[2rem] border-4 transition-all ${productPrice === price.toString() ? 'border-blue-600 bg-white scale-[1.05] shadow-xl' : 'border-slate-50 bg-slate-50 hover:bg-white'}`}>
                      <span className={`text-2xl font-black ${productPrice === price.toString() ? 'text-blue-600' : 'text-slate-700'}`}>{price}</span><br />
                      <span className="text-[10px] font-black text-slate-400 uppercase">TL</span>
                    </button>
                  ))}
                  <div className={`p-6 rounded-[2rem] border-4 transition-all flex flex-col items-center justify-center relative ${productPrice && !priceOptions.map(String).includes(productPrice) ? 'border-blue-600 bg-white scale-[1.05] shadow-xl' : 'border-slate-50 bg-slate-50 hover:bg-white focus-within:border-blue-300'}`}>
                    <div className="text-center w-full">
                      <input
                        type="text"
                        inputMode="numeric"
                        value={!priceOptions.map(String).includes(productPrice) ? productPrice : ''}
                        onChange={(e) => setProductPrice(e.target.value.replace(/[^0-9]/g, ''))}
                        placeholder="MANUEL"
                        className="w-full bg-transparent text-2xl font-black text-slate-700 text-center outline-none placeholder:text-slate-300"
                      />
                      <div className="text-[10px] font-black text-slate-400 uppercase">TL GİRİN</div>
                    </div>
                    {productPrice && !priceOptions.map(String).includes(productPrice) && (
                      <button
                        onClick={() => completeStepSelection(5, 6)}
                        className="absolute -bottom-3 left-1/2 -translate-x-1/2 bg-blue-600 text-white px-4 py-1.5 rounded-full text-[10px] font-black uppercase shadow-lg animate-in zoom-in-95"
                      >
                        ONAYLA
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex-1 bg-white shadow-sm p-8 border border-slate-100 flex flex-col min-h-0 relative">
              <div className="flex justify-between items-center mb-6 shrink-0">
                <div className="flex items-center gap-4">
                  <h4 className="text-base font-black text-slate-700 uppercase tracking-tight">ÇUVAL ÜRÜN LİSTESİ</h4>
                </div>
                <span className="bg-slate-100 text-slate-500 px-3 py-1 rounded-full text-xs font-black">{productCount} ÜRÜN</span>
              </div>

              <div className="flex-1 overflow-auto min-h-0">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b-2 border-slate-110 text-slate-400">
                      <th className="py-4 text-[10px] font-black uppercase tracking-widest">ÜRÜN</th>
                      <th className="py-4 text-[10px] font-black uppercase tracking-widest text-center">BEDEN</th>
                      <th className="py-4 text-[10px] font-black uppercase tracking-widest">KALİTE</th>
                      <th className="py-4 text-[10px] font-black uppercase tracking-widest">AĞIRLIK</th>
                      <th className="py-4 text-[10px] font-black uppercase tracking-widest">EPC</th>
                      <th className="py-4 text-[10px] font-black uppercase tracking-widest text-right">İŞLEM</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {addedVariants.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="py-12 text-center text-slate-300 font-bold uppercase tracking-widest border-2 border-dashed border-slate-50 rounded-2xl">LİSTE BOŞ</td>
                      </tr>
                    ) : (
                      addedVariants.map((item, idx) => (
                        <tr key={idx} className="group hover:bg-slate-50/80 transition-colors">
                          <td className="py-4 font-bold text-slate-800 flex items-center gap-3">
                            <span className="text-xl">{categories.find(c => c.label === item.category)?.icon || '📦'}</span>
                            <div className="flex flex-col">
                              <span className="text-sm font-black text-slate-800 leading-tight uppercase">{item.productName}</span>
                              <span className="text-[9px] font-bold text-slate-400 uppercase tracking-tighter">{item.stockName}</span>
                            </div>
                          </td>
                          <td className="py-4 font-black text-blue-600 text-center">
                            <span className="px-3 py-1.5 bg-blue-50 text-blue-700 rounded-xl font-black text-sm">{item.size}</span>
                          </td>
                          <td className="py-4 font-black text-slate-800">
                            <span className="w-6 h-6 inline-flex items-center justify-center bg-slate-100 rounded-full text-xs mr-2">{item.quality}</span>
                            Kalite
                          </td>
                          <td className="py-4 font-black text-slate-800">{item.weight} GR</td>
                          <td className="py-4 font-mono text-xs font-bold text-slate-400 truncate max-w-[150px]">{item.epc}</td>
                          <td className="py-4 text-right">
                            <button
                              onClick={() => removeVariant(idx)}
                              className="p-2 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Right Info Sidebar */}
        <div className="w-full lg:w-[380px] flex flex-col gap-6 min-h-0 shrink-0">
          {/* Current Selection & Input */}
          <div className="bg-white shadow-xl p-6 border border-slate-100 shadow-sm flex flex-col h-full min-h-[72vh]">
            <h4 className="text-xs font-black text-slate-400 mb-4 tracking-[0.2em]">Seçili Ürün Bilgisi</h4>



            <div className="space-y-2 mb-3 flex-1 overflow-y-auto pr-1">
              {/* Active Bag Info Card - Always visible */}
              <div className="p-4 bg-white border-2 border-slate-100 rounded-[2rem] shadow-xl shadow-slate-200/50 mb-3">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center text-blue-600">
                    <Package className="w-4 h-4" />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest leading-none mb-0.5">AKTİF ÇUVAL</span>
                    <span className="text-[10px] font-black text-slate-800 leading-tight break-all">
                      {selectedBag ? selectedBag.epc : '-'}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-2 bg-slate-50 rounded-2xl p-2.5 border border-slate-100">
                  <div className="text-center">
                    <div className="text-[8px] font-bold text-slate-400 uppercase mb-0.5">Adet</div>
                    <div className="text-xs font-black text-slate-700">{selectedBag ? productCount : 0}</div>
                  </div>
                  <div className="text-center border-x border-slate-200">
                    <div className="text-[8px] font-bold text-slate-400 uppercase mb-0.5">Kalan (KG)</div>
                    <div className="text-[10px] font-black text-red-600 leading-tight">
                      {remainingWeight.toFixed(2)}
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-[8px] font-bold text-slate-400 uppercase mb-0.5">Toplam (KG)</div>
                    <div className="text-xs font-black text-slate-700">{expectedWeight.toFixed(0)}</div>
                  </div>
                </div>
              </div>

              {[
                { label: 'Stok Kartı', value: stockCards.find(c => c.id === selectedStockCard)?.stockName, icon: '📑' },
                { label: 'Cinsiyet', value: selectedGender, icon: '👤' },
                { label: 'Kategori', value: selectedCategory, icon: '📂' },
                { label: 'Ürün Türü', value: selectedSubCategory, icon: '🏷️' },
                { label: 'Kalite', value: selectedQuality ? `${selectedQuality}. Kalite` : '', icon: '⭐' },
                { label: 'Beden', value: selectedSize, icon: '📏' },
              ].map((item, i) => (
                <div key={i} className={`flex items-center gap-3 p-3 rounded-2xl border-2 transition-all ${item.value ? 'bg-white border-slate-100 shadow-sm' : 'bg-slate-50 border-slate-50'}`}>
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-lg ${item.value ? 'bg-slate-50' : 'bg-white'}`}>
                    {item.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="text-[8px] font-black text-slate-400 uppercase block tracking-widest mb-0.5 leading-none">{item.label}</span>
                    <span className={`text-[11px] font-black uppercase truncate block leading-tight ${item.value ? 'text-slate-800' : 'text-slate-300'}`}>
                      {item.value || ''}
                    </span>
                  </div>
                  {item.value && (
                    <div className="w-5 h-5 rounded-full bg-green-50 flex items-center justify-center text-green-500">
                      <Check className="w-3 h-3" />
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="space-y-3 mt-auto shrink-0">
              <div className="bg-white border-2 border-slate-50 rounded-[2rem] p-4 shadow-2xl shadow-slate-200/50 space-y-3">
                <div className="relative">
                  <input
                    type="text"
                    inputMode="decimal"
                    value={productWeight > 0 ? productWeight.toString() : ''}
                    onChange={(e) => setProductWeight(Number(e.target.value.replace(/[^0-9.]/g, '')))}
                    placeholder="000"
                    className="w-full px-4 py-4 bg-slate-50 border-2 border-slate-100 rounded-2xl text-4xl font-black outline-none focus:bg-white focus:border-blue-500 transition-all placeholder:text-slate-200 text-slate-800 text-center"
                  />
                  <div className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-300 font-black text-sm uppercase">GRAM</div>
                </div>

                <div className="relative">
                  <input
                    type="text"
                    value={productEpc}
                    onChange={(e) => setProductEpc(e.target.value)}
                    placeholder="EPC"
                    className="w-full px-4 py-2.5 bg-slate-50 border-2 border-slate-100 rounded-xl text-xs font-bold outline-none focus:bg-white focus:border-blue-500 transition-all placeholder:text-slate-300 text-slate-600"
                  />
                </div>

                <button
                  onClick={handleNextStep}
                  disabled={!selectedBag || !selectedStockCard || !selectedGender || !selectedCategory || !selectedQuality || !selectedSize || productWeight <= 0 || !productEpc.trim()}
                  className={`w-full py-4 rounded-2xl text-base font-black uppercase shadow-xl active:scale-95 transition-all flex items-center justify-center gap-3 ${!selectedBag || !selectedStockCard || !selectedGender || !selectedCategory || !selectedQuality || !selectedSize || productWeight <= 0 || !productEpc.trim()
                    ? 'bg-slate-100 text-slate-300 cursor-not-allowed shadow-none'
                    : 'bg-blue-600 text-white hover:bg-blue-500 hover:shadow-blue-500/20'
                    }`}
                >
                  <Printer className="w-5 h-5" />
                  <span>YAZDIR</span>
                </button>
              </div>
            </div>



            {showApprovalSummary && (
              <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-xl flex items-center justify-center z-[100] p-4">
                <div className="bg-white rounded-[3rem] w-full max-w-lg p-8 space-y-6 shadow-2xl animate-in zoom-in-95">
                  <div className="text-center">
                    <div className="text-5xl mb-4">📝</div>
                    <h3 className="text-2xl font-black text-slate-900 uppercase">SON ONAY VE BALANS</h3>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-slate-50 rounded-[2rem] border text-center">
                      <p className="text-[9px] font-black text-slate-300 uppercase mb-1">TOPLAM ÜRÜN</p>
                      <p className="text-2xl font-black text-slate-900">{productCount} ADET</p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-[2rem] border text-center">
                      <p className="text-[9px] font-black text-slate-300 uppercase mb-1">NET AĞIRLIK</p>
                      <p className="text-2xl font-black text-blue-600">{(totalWeight / 1000).toFixed(2)} KG</p>
                    </div>
                  </div>

                  <div className="flex flex-col items-center p-4 bg-blue-50/50 border-2 border-blue-100 rounded-[2.5rem] relative overflow-hidden text-center">
                    <div className="absolute top-0 right-0 p-2 opacity-10">
                      <Printer className="w-16 h-16 text-blue-600" />
                    </div>
                    <p className="text-[10px] font-black text-blue-400 uppercase mb-1">ÜRETİLEN ÇUVAL ETİKETİ</p>
                    <p className="text-3xl font-black text-blue-700 font-mono tracking-wider">{generatedBagTag}</p>
                    <div className="mt-2 flex items-center gap-1.5 px-3 py-1 bg-blue-100 rounded-full">
                      <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
                      <span className="text-[8px] font-black text-blue-600 uppercase">YAZICIYA GÖNDERİLECEK</span>
                    </div>
                  </div>

                  <div className="p-6 bg-orange-50/50 rounded-[2.5rem] border-2 border-orange-100 text-center">
                    <label className="block text-[10px] font-black text-orange-400 mb-4 uppercase">KAYDEDİLECEK BALANS DEĞERİ (KG)</label>
                    <div className="relative mb-4">
                      <input
                        type="number"
                        step="0.01"
                        value={manualBalance}
                        onChange={(e) => setManualBalance(Number(e.target.value))}
                        className="w-full bg-white border-2 border-orange-200 rounded-2xl py-4 text-4xl font-black text-center outline-none focus:border-orange-400 transition-colors"
                      />
                      <div className="absolute right-6 top-1/2 -translate-y-1/2 text-orange-200 text-xl font-black">KG</div>
                    </div>

                    <div className="inline-block px-4 py-2 bg-white rounded-xl border-2 border-orange-100 shadow-sm">
                      <span className="text-[10px] font-bold text-slate-400 uppercase mr-2">GEREKLİ FARK:</span>
                      <span className="text-lg font-black text-orange-600">{(expectedWeight - (totalWeight / 1000)).toFixed(2)} KG</span>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <button onClick={() => setShowApprovalSummary(false)} className="flex-1 py-4 rounded-2xl bg-slate-100 font-black uppercase text-xs text-slate-400 hover:bg-slate-200 transition-colors">Vazgeç</button>
                    <button
                      disabled={!selectedBag || Math.abs(((totalWeight / 1000) + manualBalance) - expectedWeight) >= 0.01}
                      onClick={() => {
                        if (selectedBag) {
                          console.log(`Etiket yazdırılıyor: ${generatedBagTag}`);
                          const card = stockCards.find(c => c.id === selectedStockCard);
                          onApprove(
                            selectedBag.id,
                            totalWeight / 1000,
                            generatedBagTag
                          );
                          setShowApprovalSummary(false);
                          setShowCloseSuccess(true);
                          setTimeout(() => {
                            setShowCloseSuccess(false);
                            setSelectedBag(null);
                            setSelectedBagId(null);
                            setAddedVariants([]);
                            setGeneratedBagTag('');
                            resetDraft();
                          }, 2000);
                        }
                      }}
                      className={`flex-[2] py-4 rounded-2xl font-black uppercase text-xs shadow-xl transition-all ${!selectedBag || Math.abs(((totalWeight / 1000) + manualBalance) - expectedWeight) >= 0.01 ? 'bg-slate-200 text-slate-400 cursor-not-allowed' : 'bg-green-600 text-white hover:bg-green-700 shadow-green-200'}`}
                    >
                      BİTİR VE YAZDIR
                    </button>
                  </div>
                </div>
              </div>
            )}

            {showCloseSuccess && (
              <div className="fixed inset-0 bg-slate-900/90 backdrop-blur-xl flex items-center justify-center z-[200] p-4">
                <div className="bg-white rounded-[3rem] p-12 flex flex-col items-center gap-8 shadow-2xl animate-in zoom-in-95 duration-500 max-w-sm w-full text-center">
                  <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center text-green-600 shadow-inner animate-bounce">
                    <Check className="w-12 h-12" />
                  </div>
                  <div className="space-y-4">
                    <h3 className="text-3xl font-black text-slate-900 uppercase leading-none">TEBRİKLER!</h3>
                    <p className="text-slate-500 font-bold uppercase tracking-widest text-sm">Çuval Başarıyla Kapatıldı</p>
                  </div>
                  <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-green-500 animate-progress-shrink" />
                  </div>
                </div>
              </div>
            )}

            {showBagReadSuccess && selectedBag && (
              <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-md flex items-center justify-center z-[100] p-4">
                <div className="bg-white rounded-3xl p-10 md:p-12 flex flex-col items-center gap-6 shadow-2xl animate-in zoom-in-95 duration-300 max-w-md w-full text-center">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center text-green-600 shadow-inner"><Check className="w-10 h-10" /></div>
                  <div className="space-y-2"><h2 className="text-xl md:text-2xl font-black text-slate-800 uppercase">Çuval Seçildi</h2><div className="text-sm md:text-lg text-blue-600 font-mono font-bold bg-blue-50 px-4 py-2 rounded-xl border border-blue-100 inline-block">{selectedBag.epc}</div><p className="text-slate-500 font-medium pt-2">Çuval seçildi, şimdi kategori adımlarına devam edebilirsiniz.</p></div>
                </div>
              </div>
            )}

            {/* Split Bag Modal */}
            {showSplitModal && splittingBagItem && (
              <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-xl flex items-center justify-center z-[200] p-4">
                <div className="bg-white rounded-[3rem] w-full max-w-md p-8 pt-12 space-y-8 shadow-2xl animate-in zoom-in-95 border-2 border-emerald-100 relative overflow-hidden">
                  <div className="absolute top-0 left-0 w-full h-2 bg-emerald-500" />
                  <div className="text-center">
                    <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center text-emerald-600 mx-auto mb-6 shadow-inner">
                      <Trash2 className="w-10 h-10 -rotate-90" />
                    </div>
                    <h3 className="text-3xl font-black text-slate-900 uppercase tracking-tight">Çuval Parçala</h3>
                  </div>

                  {/* Weight Summary Display */}
                  <div className="grid grid-cols-2 gap-3 pb-2">
                    <div className="bg-slate-50 border border-slate-100 rounded-[2rem] p-4 text-center">
                      <p className="text-[9px] font-black text-slate-300 uppercase mb-1">Mevcut Ağırlık</p>
                      <p className="text-lg font-black text-slate-600">{(splittingBagItem.weight || 0).toFixed(2)} KG</p>
                    </div>
                    <div className="bg-emerald-50/50 border border-emerald-100 rounded-[2.5rem] p-4 text-center">
                      <p className="text-[9px] font-black text-emerald-400 uppercase mb-1">Yeni Çuval (Ayrılacak)</p>
                      <p className="text-xl font-black text-emerald-600">{Number(splitWeightInput || 0).toFixed(2)} KG</p>
                    </div>
                    {splitWeightInput && Number(splitWeightInput) > 0 && (
                      <div className="col-span-2 bg-blue-50/30 border border-blue-100/50 rounded-[2rem] p-4 text-center mt-1 animate-in zoom-in-95">
                        <p className="text-[9px] font-black text-blue-300 uppercase mb-1">Mevcut Çuvalda Kalacak (Kalan)</p>
                        <p className="text-xl font-black text-blue-600 leading-none">
                          {((splittingBagItem.weight || 0) - Number(splitWeightInput)).toFixed(2)} KG
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="space-y-4">
                    <label className="block text-[11px] font-black text-slate-400 uppercase tracking-widest text-center px-4 leading-relaxed">
                      LÜTFEN BU ÇUVALDAN AYRILACAK AĞIRLIĞI GİRİNİZ (KG)
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        autoFocus
                        value={splitWeightInput}
                        onChange={(e) => setSplitWeightInput(e.target.value)}
                        placeholder="0.00"
                        className="w-full bg-slate-50 border-4 border-slate-100 rounded-3xl py-8 text-6xl font-black text-center text-emerald-600 outline-none focus:border-emerald-200 focus:bg-white transition-all shadow-inner"
                      />
                      <div className="absolute right-8 top-1/2 -translate-y-1/2 text-2xl font-black text-slate-200">KG</div>
                    </div>
                  </div>

                  <div className="flex gap-4 pt-4">
                    <button
                      onClick={() => { setShowSplitModal(false); setSplittingBagItem(null); }}
                      className="flex-1 py-5 rounded-2xl bg-slate-100 font-extrabold uppercase text-sm text-slate-500 hover:bg-slate-200 transition-all border border-slate-200/50"
                    >
                      İptal
                    </button>
                    <button
                      onClick={confirmSplit}
                      disabled={!splitWeightInput || Number(splitWeightInput) >= (splittingBagItem.weight || 0)}
                      className={`flex-[2] py-5 rounded-2xl font-black uppercase text-sm shadow-xl transition-all ${!splitWeightInput || Number(splitWeightInput) >= (splittingBagItem.weight || 0)
                          ? 'bg-slate-200 text-slate-400 cursor-not-allowed border-none shadow-none'
                          : 'bg-emerald-600 text-white hover:bg-emerald-700 shadow-emerald-200 hover:-translate-y-1'
                        }`}
                    >
                      PARÇALAMAYI ONAYLA
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* All Stock Cards Modal */}
            {showAllStockCards && (
              <div className="fixed inset-0 bg-slate-950/90 backdrop-blur-xl flex items-center justify-center z-[150] p-4" onClick={() => setShowAllStockCards(false)}>
                <div className="bg-white rounded-[3rem] w-full max-w-5xl p-8 md:p-12 shadow-2xl animate-in zoom-in-95 duration-300 max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-between mb-8">
                    <div>
                      <h3 className="text-3xl font-black text-slate-800 uppercase tracking-tight">TÜM STOK KARTLARI</h3>
                      <p className="text-sm font-bold text-slate-400 mt-1">{filteredStockCards.length} adet kart listeleniyor</p>
                    </div>
                    <button onClick={() => setShowAllStockCards(false)} className="w-14 h-14 bg-slate-100 hover:bg-red-50 rounded-2xl flex items-center justify-center text-slate-400 hover:text-red-500 transition-all group">
                      <X className="w-6 h-6 group-hover:rotate-90 transition-transform duration-300" />
                    </button>
                  </div>

                  <div className="relative mb-8">
                    <Search className="absolute left-6 top-1/2 -translate-y-1/2 w-6 h-6 text-slate-300" />
                    <input
                      type="text"
                      value={stockCardSearch}
                      onChange={(e) => setStockCardSearch(e.target.value)}
                      onFocus={() => setShowKeyboard(true)}
                      placeholder="Stok adı veya kodu ile arama yapın..."
                      className="w-full pl-16 pr-6 py-6 bg-slate-50 border-2 border-slate-100 rounded-[2rem] text-lg font-bold text-slate-700 focus:border-blue-500 focus:bg-white outline-none transition-all placeholder:text-slate-300 shadow-inner"
                    />
                  </div>

                  <div className="relative flex items-center gap-4 flex-1 min-h-0">
                    <button onClick={() => scrollStockCards('left')} className="shrink-0 w-14 h-14 bg-white border-2 border-slate-100 hover:border-blue-500 hover:bg-blue-50 rounded-2xl flex items-center justify-center text-slate-400 hover:text-blue-600 transition-all shadow-lg z-10">
                      <ChevronLeft className="w-8 h-8" />
                    </button>

                    <div
                      ref={scrollContainerRef}
                      className="flex-1 overflow-x-auto overflow-y-hidden flex gap-6 py-4 px-2 scroll-smooth no-scrollbar"
                      style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                    >
                      {filteredStockCards.map(card => (
                        <button
                          key={card.id}
                          onClick={() => { setSelectedStockCard(card.id); setShowAllStockCards(false); completeStepSelection(1, 2); }}
                          className={`shrink-0 w-[260px] p-8 rounded-[2.5rem] border-4 text-left transition-all hover:scale-[1.03] ${selectedStockCard === card.id
                            ? 'border-blue-600 bg-blue-50/20 shadow-xl shadow-blue-200/50'
                            : 'border-slate-50 bg-white hover:border-blue-200'}`}
                        >
                          <div className={`w-14 h-14 rounded-2xl flex items-center justify-center text-3xl mb-6 shadow-sm ${selectedStockCard === card.id ? 'bg-blue-600 text-white' : 'bg-slate-50'}`}>📑</div>
                          <span className="text-xs font-black text-slate-400 uppercase tracking-widest block mb-1">{card.stockCode}</span>
                          <div className="text-xl font-black text-slate-800 leading-tight h-14 line-clamp-2">{card.stockName}</div>
                          {selectedStockCard === card.id && (
                            <div className="mt-6 flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded-full w-fit">
                              <Check className="w-4 h-4" />
                              <span className="text-[10px] font-black uppercase tracking-widest text-white">SEÇİLİ</span>
                            </div>
                          )}
                        </button>
                      ))}
                    </div>

                    <button onClick={() => scrollStockCards('right')} className="shrink-0 w-14 h-14 bg-white border-2 border-slate-100 hover:border-blue-500 hover:bg-blue-50 rounded-2xl flex items-center justify-center text-slate-400 hover:text-blue-600 transition-all shadow-lg z-10">
                      <ChevronRight className="w-8 h-8" />
                    </button>
                  </div>

                  {/* Virtual Keyboard */}
                  {showKeyboard && (
                    <div className="mt-4 bg-slate-100 rounded-[2rem] p-4 animate-in slide-in-from-bottom-4 duration-300 shrink-0">
                      <div className="flex flex-col gap-2">
                        {/* Number Row */}
                        <div className="flex gap-1.5 justify-center">
                          {['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'].map(key => (
                            <button key={key} onClick={() => setStockCardSearch(prev => prev + key)} className="w-12 h-12 bg-white rounded-xl text-lg font-bold text-slate-700 hover:bg-blue-50 hover:text-blue-600 active:bg-blue-100 transition-all shadow-sm border border-slate-200">{key}</button>
                          ))}
                        </div>
                        {/* Row 1 */}
                        <div className="flex gap-1.5 justify-center">
                          {['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P', 'Ğ', 'Ü'].map(key => (
                            <button key={key} onClick={() => setStockCardSearch(prev => prev + (capsLock ? key : key.toLocaleLowerCase('tr')))} className="w-12 h-12 bg-white rounded-xl text-lg font-bold text-slate-700 hover:bg-blue-50 hover:text-blue-600 active:bg-blue-100 transition-all shadow-sm border border-slate-200">{capsLock ? key : key.toLocaleLowerCase('tr')}</button>
                          ))}
                        </div>
                        {/* Row 2 */}
                        <div className="flex gap-1.5 justify-center">
                          {['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L', 'Ş', 'İ', ','].map(key => (
                            <button key={key} onClick={() => setStockCardSearch(prev => prev + (capsLock ? key : key.toLocaleLowerCase('tr')))} className="w-12 h-12 bg-white rounded-xl text-lg font-bold text-slate-700 hover:bg-blue-50 hover:text-blue-600 active:bg-blue-100 transition-all shadow-sm border border-slate-200">{capsLock ? key : key.toLocaleLowerCase('tr')}</button>
                          ))}
                        </div>
                        {/* Row 3 */}
                        <div className="flex gap-1.5 justify-center">
                          <button onClick={() => setCapsLock(prev => !prev)} className={`w-16 h-12 rounded-xl text-sm font-black transition-all shadow-sm flex items-center justify-center ${capsLock ? 'bg-green-500 text-white border border-green-600' : 'bg-slate-200 text-slate-600 border border-slate-300 hover:bg-slate-300'}`}>
                            ⇧
                          </button>
                          {['Z', 'X', 'C', 'V', 'B', 'N', 'M', 'Ö', 'Ç', '.'].map(key => (
                            <button key={key} onClick={() => setStockCardSearch(prev => prev + (capsLock ? key : key.toLocaleLowerCase('tr')))} className="w-12 h-12 bg-white rounded-xl text-lg font-bold text-slate-700 hover:bg-blue-50 hover:text-blue-600 active:bg-blue-100 transition-all shadow-sm border border-slate-200">{capsLock ? key : key.toLocaleLowerCase('tr')}</button>
                          ))}
                          <button onClick={() => setStockCardSearch(prev => prev.slice(0, -1))} className="w-16 h-12 bg-amber-50 rounded-xl text-sm font-black text-amber-700 hover:bg-amber-100 active:bg-amber-200 transition-all shadow-sm border border-amber-200 flex items-center justify-center">
                            ← Sil
                          </button>
                        </div>
                        {/* Bottom Row */}
                        <div className="flex gap-1.5 justify-center">
                          <button onClick={() => setStockCardSearch('')} className="w-24 h-12 bg-red-50 rounded-xl text-xs font-black text-red-600 hover:bg-red-100 active:bg-red-200 transition-all shadow-sm border border-red-200">Temizle</button>
                          <button onClick={() => setStockCardSearch(prev => prev + ' ')} className="flex-1 max-w-[300px] h-12 bg-white rounded-xl text-sm font-bold text-slate-500 hover:bg-blue-50 active:bg-blue-100 transition-all shadow-sm border border-slate-200">Boşluk</button>
                          <button onClick={() => setShowKeyboard(false)} className="w-24 h-12 bg-blue-600 rounded-xl text-xs font-black text-white hover:bg-blue-700 active:bg-blue-800 transition-all shadow-sm">Kapat</button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div >
  );
};

export default BagDetailsPage;
